import Vue from 'vue';

Vue.component('configurator-edition-select', {
    props: {
        editions: {
            type: Array,
            required: true,
        },
        initialEdition: {
            type: Object,
            required: true,
        },
        noRedWithRed: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            selectionExpanded: false,
            selectedIndex: 0,
            edition: null,
            disclaimer: '',
            options: [],
            selectedOptions: [],
            showSardanaRed: true,
            parsedEditions: null,
            optionHandles: [],
            optionsAvailable: [],
            interiorOptionsExpanded: false,
            color: null,
        };
    },

    computed: {
        toggleConfiguratorBoxEdition() {
            return this.selectionExpanded ? 'active' : '';
        },

        toggleConfiguratorInteriorSelect() {
            if (this.optionsAvailable.length < 1) return '';
            return this.interiorOptionsExpanded ? 'active' : '';
        },

        computedSelectedInteriorOption() {
            // First check if any option is already selected
            const selectedOpt = this.options.find((opt) => opt.selected);
            if (selectedOpt) return selectedOpt;

            // Then check for default selected by CMS that is available
            const defaultSelectedByCms = this.options.find(
                (opt) =>
                    parseInt(opt.field_selectedByDefault) &&
                    this.isInteriorOptionAvailable(opt),
            );
            if (defaultSelectedByCms) return defaultSelectedByCms;

            // Finally, return first available option or null
            return (
                this.options.find((opt) =>
                    this.isInteriorOptionAvailable(opt),
                ) || null
            );
        },
    },

    watch: {
        /**
         * Set edition when index is updated.
         */
        selectedIndex(value) {
            this.edition = this.editions[value];
        },

        /**
         * Send event on edition change and update options object.
         */
        edition(value) {
            // Emit edition change event
            EventBus.$emit('configurator-set-edition', value);

            try {
                this.trackEditionSelection();
            } catch (error) {
                console.error(error);
            }

            // Initialize options with default values
            this.options = value.field_commerceOptions.map((opt) => ({
                ...opt,
                selected: false,
                priceAddition: parseFloat(opt.priceValue) || 0,
            }));

            // Update available options based on color constraints
            this.checkDisabledColors();

            // Find and select the default option
            this.selectDefaultInteriorOption();

            // Update option handles for reference
            this.optionHandles = this.options.map(
                (option) => option.field_optionHandle,
            );

            // Emit event for changed options
            this.$nextTick(() => {
                EventBus.$emit(
                    'configurator-set-edition-options',
                    this.options,
                );
            });
        },
    },

    mounted() {
        this.initializeEdition();
        this.setupEventListeners();
    },

    methods: {
        /**
         * Initialize the component with the initial edition
         */
        initializeEdition() {
            this.edition = this.initialEdition;

            // Find the index of the initial edition
            const editionIndex = this.editions.findIndex(
                (edition) =>
                    edition.field_commerceHandle ===
                    this.initialEdition.field_commerceHandle,
            );

            if (editionIndex !== -1) {
                this.selectedIndex = editionIndex;
            }

            EventBus.$emit('configurator-set-edition', this.initialEdition);
        },

        /**
         * Set up all event listeners
         */
        setupEventListeners() {
            this.setupPriceAdditionListener();
            this.setupPaymentOptionListener();
            this.setupColorChangeListener();
        },

        /**
         * Listen for price addition events
         */
        setupPriceAdditionListener() {
            EventBus.$on(
                'configurator-option-price-addition',
                (
                    optionHandle,
                    price,
                    formattedPrice,
                    shopPrice,
                    showPriceImpact,
                ) => {
                    const optionIndex = this.options.findIndex(
                        (option) => option.field_optionHandle === optionHandle,
                    );

                    if (optionIndex === -1) return;

                    // Update price in option object
                    const updatedOption = { ...this.options[optionIndex] };
                    updatedOption.priceAddition = '';

                    if (price && showPriceImpact) {
                        updatedOption.priceAddition = formattedPrice;
                        updatedOption.shopPriceAddition = shopPrice;
                    }

                    // Use Vue.set to ensure reactivity
                    Vue.set(this.options, optionIndex, updatedOption);
                },
            );
        },

        /**
         * Listen for payment option changes
         */
        setupPaymentOptionListener() {
            EventBus.$on('configurator-set-payment-option', (paymentOption) => {
                this.disclaimer =
                    paymentOption.field_paymentOption_editionDisclaimer || '';
            });
        },

        /**
         * Listen for color change events
         */
        setupColorChangeListener() {
            EventBus.$on('configurator-set-color', (color) => {
                this.color = color;
                this.checkDisabledColors();
                this.handleSardanaRedVisibility(color);
            });
        },

        /**
         * Handle special case for Sardana Red interior with red exterior colors
         */
        handleSardanaRedVisibility(color) {
            const isRedExterior = ['phantom-red', 'red'].includes(
                color.colorSlug,
            );
            const shouldHideSardanaRed = isRedExterior && this.noRedWithRed;

            // Update visibility flag
            this.showSardanaRed = !shouldHideSardanaRed;

            if (!shouldHideSardanaRed) {
                return;
            }

            // Find and deselect red interior if it's currently selected
            const redOption = this.options.find(
                (opt) => opt.field_optionHandle === 'redinterior',
            );

            if (!redOption || !redOption.selected) {
                return;
            }

            redOption.selected = false;

            // Remove from selected options array
            const redIndex = this.selectedOptions.indexOf('redinterior');
            if (redIndex !== -1) {
                this.selectedOptions.splice(redIndex, 1);
            }

            // Emit change event
            this.emitOptionChange(redOption);

            // Select a new default option since red is now unavailable
            this.selectDefaultInteriorOption();
        },

        /**
         * Selects the default interior option based on availability and CMS settings
         */
        selectDefaultInteriorOption() {
            let defaultSelectedHandle = null;
            let foundDefault = false;

            // First try to find a default option marked in CMS that is available
            for (let opt of this.options) {
                const isDefault = parseInt(opt.field_selectedByDefault)
                    ? true
                    : false;
                if (
                    isDefault &&
                    !foundDefault &&
                    this.isInteriorOptionAvailable(opt)
                ) {
                    opt.selected = true;
                    defaultSelectedHandle = opt.field_optionHandle;
                    foundDefault = true;
                } else {
                    opt.selected = false;
                }
            }

            // If no default found, select first available option
            if (!foundDefault) {
                for (let opt of this.options) {
                    if (this.isInteriorOptionAvailable(opt)) {
                        opt.selected = true;
                        defaultSelectedHandle = opt.field_optionHandle;
                        break;
                    }
                }
            }

            // Ensure all other options are deselected
            this.options.forEach((opt) => {
                if (opt.field_optionHandle !== defaultSelectedHandle) {
                    opt.selected = false;
                }
            });

            // Update selectedOptions array
            this.selectedOptions = defaultSelectedHandle
                ? [defaultSelectedHandle]
                : [];
        },

        /**
         * Checks if an interior option is available based on current color and configuration
         */
        isInteriorOptionAvailable(option) {
            if (!option) return false;

            // Check for Sardana Red special case
            if (
                option.field_optionHandle &&
                option.field_optionHandle.toLowerCase().includes('sardana') &&
                !this.showSardanaRed
            ) {
                return false;
            }

            // Check if option is in available options list
            return this.optionsAvailable.includes(option.field_optionHandle);
        },
        /**
         * Handles selection of a single interior option
         */
        handleSingleInteriorSelect(selectedOption) {
            if (!this.isInteriorOptionAvailable(selectedOption)) return;

            // Update selection state for all options
            this.options.forEach((opt) => {
                opt.selected =
                    opt.field_optionHandle ===
                    selectedOption.field_optionHandle;
            });

            // Update selectedOptions array
            this.selectedOptions = [selectedOption.field_optionHandle];

            // Emit events
            this.emitOptionChange(selectedOption);
            this.emitHandleExcludeInterior(this.options);
        },

        /**
         * Checks if an interior color option exists in available options
         */
        checkIfInteriorColorExists(optionHandle) {
            return this.optionsAvailable.includes(optionHandle);
        },

        /**
         * Updates the available interior options based on color constraints
         */
        checkDisabledColors() {
            // Start with all options as available
            this.optionsAvailable = this.options.map(
                (option) => option.field_optionHandle,
            );

            // Apply color-based restrictions if applicable
            if (this.color && this.color.field_disableInteriorColors_gqcddajo) {
                try {
                    const parsedColorsWithEditions = JSON.parse(
                        this.color.field_disableInteriorColors_gqcddajo,
                    );

                    // Process each color-edition restriction
                    parsedColorsWithEditions.forEach((colorWithEdition) => {
                        const colorWithEditionValue =
                            Object.values(colorWithEdition)[0];
                        const disabledParts = colorWithEditionValue.split('-');

                        if (disabledParts.length !== 2) return;

                        const [disabledEdition, disabledColor] = disabledParts;

                        // Apply restriction if it matches current edition
                        if (disabledEdition === this.edition.title) {
                            const indexToRemove =
                                this.optionsAvailable.indexOf(disabledColor);
                            if (indexToRemove !== -1) {
                                this.optionsAvailable.splice(indexToRemove, 1);
                            }
                        }
                    });
                } catch (error) {
                    console.error('Error parsing color restrictions:', error);
                }
            }
        },

        /**
         * Parse edition features from JSON
         */
        parseEditionFeatures(index) {
            return JSON.parse(this.editions[index].field_commerceFeatures);
        },

        /**
         * Returns CSS class for selected switch button
         */
        toggleSwitchButton(index) {
            return this.selectedIndex === index
                ? 'switch-button--selected'
                : '';
        },

        /**
         * Returns CSS class for selected switch text
         */
        toggleConfiguratorSwitchSelect(index) {
            return this.selectedIndex === index
                ? 'configurator-switch-select__text--visible'
                : '';
        },

        /**
         * Handle edition selection and animate height transition
         */
        handleSetSelectedEdition(index) {
            const allEditionTexts = document.querySelectorAll(
                '#configurator-edition__text',
            );

            // Collapse current selection
            if (allEditionTexts[this.selectedIndex]) {
                allEditionTexts[this.selectedIndex].style.height = 0;
            }

            // Update selection and expand new selection
            this.selectedIndex = index;

            if (allEditionTexts[this.selectedIndex]) {
                allEditionTexts[this.selectedIndex].style.height = 'auto';
            }
        },

        /**
         * Legacy method for option selection - redirects to single select handler
         * @deprecated Use handleSingleInteriorSelect instead
         */
        onOptionSelectionChange(optionParam) {
            // For safety, check if this is an interior option
            const isInterior = this.options.find(
                (o) => o.field_optionHandle === optionParam.field_optionHandle,
            );

            if (!isInterior) return;

            this.handleSingleInteriorSelect(optionParam);
        },

        /**
         * Emit events when an option's selection state changes
         */
        emitOptionChange(option) {
            EventBus.$emit('configurator-set-edition-option', option);
            EventBus.$emit('configurator-set-edition-options', this.options);
        },

        /**
         * Emit event for excluding interior options
         */
        emitHandleExcludeInterior(options) {
            EventBus.$emit('configurator-set-exclude-interior', options);
        },

        /**
         * Track edition selection in analytics
         */
        trackEditionSelection() {
            if (typeof p161 !== 'object') return;

            // Determine pixel ID based on site
            const pixelIds = {
                mgMotorsNetherlands: '3161292',
                mgMotorsNorway: '3161321',
            };

            const pixelId = pixelIds[this.$parent.siteHandle] || '';

            if (!pixelId) return;

            const trackData = {
                customer_extra: [
                    this.$parent.model,
                    this.$parent.configuratorResult.color.key,
                    this.edition.field_commerceHandle,
                ].join('_'),
            };

            p161.track(pixelId, trackData);

            if (typeof window.trackQueue === 'function') {
                window.trackQueue(p161);
            }
        },
    },
});
